import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { X, Brain, Zap, TrendingDown, Info, Sparkles } from 'lucide-react';
import { DCAFrequency, FREQUENCY_LABELS, DCACreateParams } from '../../types/dca';
import { toast } from 'sonner';

interface DCACreateFormProps {
  onClose: () => void;
}

// Popular tokens for DCA
const AVAILABLE_TOKENS = [
  { address: '0xA0b86991c6218b36c1d19D4a2e9Eb0cE3606eB48', symbol: 'USDC', name: 'USD Coin' },
  { address: '0xdAC17F958D2ee523a2206206994597C13D831ec7', symbol: 'USDT', name: 'Tether USD' },
  { address: '0x6B175474E89094C44Da98b954EescdeCB5cAd7fBf', symbol: 'DAI', name: 'Dai Stablecoin' },
  { address: '0x0000000000000000000000000000000000000000', symbol: 'ETH', name: 'Ethereum' },
  { address: '0x2260FAC5E5542a773Aa44fBCfeDf7C193bc2C599', symbol: 'WBTC', name: 'Wrapped Bitcoin' },
  { address: '0x1234567890123456789012345678901234567890', symbol: 'NDH', name: 'Nardium Token' },
  { address: '0x514910771AF9Ca656af840dff83E8264EcF986CA', symbol: 'LINK', name: 'Chainlink' },
  { address: '0x7Fc66500c84A76Ad7e9c93437bFc5Ac33E2DDaE9', symbol: 'AAVE', name: 'Aave' },
];

export function DCACreateForm({ onClose }: DCACreateFormProps) {
  const [formData, setFormData] = useState<Partial<DCACreateParams>>({
    name: '',
    sourceToken: 'USDC',
    targetToken: '',
    investmentAmount: '',
    totalBudget: '',
    frequency: 'weekly',
    smartTiming: true,
    dipBuying: false,
    dipPercentage: 5,
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name || !formData.targetToken || !formData.investmentAmount || !formData.totalBudget) {
      toast.error('Por favor completa todos los campos requeridos');
      return;
    }

    if (parseFloat(formData.investmentAmount) > parseFloat(formData.totalBudget)) {
      toast.error('El monto por compra no puede ser mayor al presupuesto total');
      return;
    }

    setIsSubmitting(true);

    try {
      // Here we would call the backend/smart contract
      console.log('Creating DCA strategy:', formData);

      // Simulate API call
      await new Promise((resolve) => setTimeout(resolve, 1500));

      toast.success('Estrategia DCA creada exitosamente!');
      onClose();
    } catch (error) {
      toast.error('Error al crear la estrategia');
      console.error(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const estimatedPurchases = formData.totalBudget && formData.investmentAmount
    ? Math.floor(parseFloat(formData.totalBudget) / parseFloat(formData.investmentAmount))
    : 0;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle>Nueva Estrategia DCA</CardTitle>
            <CardDescription>Configura tu estrategia de Dollar Cost Averaging</CardDescription>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose}>
            <X className="h-4 w-4" />
          </Button>
        </CardHeader>

        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Strategy Name */}
            <div className="space-y-2">
              <Label htmlFor="name">Nombre de la Estrategia</Label>
              <Input
                id="name"
                placeholder="Ej: Acumulacion ETH Semanal"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
            </div>

            {/* Token Selection */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Token Origen</Label>
                <Select
                  value={formData.sourceToken}
                  onValueChange={(value) => setFormData({ ...formData, sourceToken: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccionar" />
                  </SelectTrigger>
                  <SelectContent>
                    {AVAILABLE_TOKENS.filter(t => ['USDC', 'USDT', 'DAI'].includes(t.symbol)).map((token) => (
                      <SelectItem key={token.symbol} value={token.symbol}>
                        {token.symbol} - {token.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Token Destino</Label>
                <Select
                  value={formData.targetToken}
                  onValueChange={(value) => setFormData({ ...formData, targetToken: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccionar" />
                  </SelectTrigger>
                  <SelectContent>
                    {AVAILABLE_TOKENS.filter(t => !['USDC', 'USDT', 'DAI'].includes(t.symbol)).map((token) => (
                      <SelectItem key={token.symbol} value={token.symbol}>
                        {token.symbol} - {token.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Investment Amount */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="amount">Monto por Compra</Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">$</span>
                  <Input
                    id="amount"
                    type="number"
                    placeholder="100"
                    className="pl-7"
                    value={formData.investmentAmount}
                    onChange={(e) => setFormData({ ...formData, investmentAmount: e.target.value })}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="budget">Presupuesto Total</Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">$</span>
                  <Input
                    id="budget"
                    type="number"
                    placeholder="5000"
                    className="pl-7"
                    value={formData.totalBudget}
                    onChange={(e) => setFormData({ ...formData, totalBudget: e.target.value })}
                  />
                </div>
              </div>
            </div>

            {/* Frequency */}
            <div className="space-y-2">
              <Label>Frecuencia de Compra</Label>
              <Select
                value={formData.frequency}
                onValueChange={(value) => setFormData({ ...formData, frequency: value as DCAFrequency })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(FREQUENCY_LABELS).map(([value, label]) => (
                    <SelectItem key={value} value={value}>
                      {label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Estimated Purchases */}
            {estimatedPurchases > 0 && (
              <div className="bg-violet-500/10 border border-violet-500/20 rounded-lg p-4">
                <div className="flex items-center gap-2 text-violet-600 dark:text-violet-400">
                  <Info className="h-4 w-4" />
                  <span className="text-sm font-medium">
                    ~{estimatedPurchases} compras programadas
                  </span>
                </div>
              </div>
            )}

            {/* Smart Features */}
            <div className="space-y-4">
              <h4 className="font-medium text-slate-900 dark:text-slate-100 flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-amber-500" />
                Funciones Inteligentes
              </h4>

              {/* Smart Timing */}
              <div className="flex items-center justify-between p-4 bg-slate-50 dark:bg-slate-800 rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-violet-500/20 rounded-lg">
                    <Brain className="h-5 w-5 text-violet-500" />
                  </div>
                  <div>
                    <p className="font-medium text-slate-900 dark:text-slate-100">Smart Timing</p>
                    <p className="text-sm text-slate-600 dark:text-slate-400">
                      IA optimiza el momento de compra para mejor precio
                    </p>
                  </div>
                </div>
                <Switch
                  checked={formData.smartTiming}
                  onCheckedChange={(checked) => setFormData({ ...formData, smartTiming: checked })}
                />
              </div>

              {/* Dip Buying */}
              <div className="p-4 bg-slate-50 dark:bg-slate-800 rounded-lg space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-green-500/20 rounded-lg">
                      <TrendingDown className="h-5 w-5 text-green-500" />
                    </div>
                    <div>
                      <p className="font-medium text-slate-900 dark:text-slate-100">Dip Buying</p>
                      <p className="text-sm text-slate-600 dark:text-slate-400">
                        Compra extra cuando el precio cae
                      </p>
                    </div>
                  </div>
                  <Switch
                    checked={formData.dipBuying}
                    onCheckedChange={(checked) => setFormData({ ...formData, dipBuying: checked })}
                  />
                </div>

                {formData.dipBuying && (
                  <div className="space-y-2 pt-2 border-t border-slate-200 dark:border-slate-700">
                    <Label className="text-sm">Porcentaje de caida para trigger</Label>
                    <div className="flex items-center gap-2">
                      <Input
                        type="number"
                        min="1"
                        max="50"
                        value={formData.dipPercentage}
                        onChange={(e) => setFormData({ ...formData, dipPercentage: parseInt(e.target.value) })}
                        className="w-20"
                      />
                      <span className="text-slate-600 dark:text-slate-400">%</span>
                    </div>
                    <p className="text-xs text-slate-500">
                      Compra adicional cuando el precio cae {formData.dipPercentage}% o mas
                    </p>
                  </div>
                )}
              </div>

              {/* Gas Optimization Info */}
              <div className="flex items-center gap-3 p-4 bg-amber-500/10 border border-amber-500/20 rounded-lg">
                <Zap className="h-5 w-5 text-amber-500" />
                <p className="text-sm text-slate-700 dark:text-slate-300">
                  Las ejecuciones se optimizan automaticamente para gas bajo
                </p>
              </div>
            </div>

            {/* Submit Buttons */}
            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                className="flex-1"
                onClick={onClose}
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                className="flex-1 bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Creando...' : 'Crear Estrategia'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
